import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Bookmark, Headphones, Home, MessageCircle, MessageSquareText, Moon, Send, ShieldCheck, Sun, UserRound } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Avatar, Presence } from "./Avatar";
import { useAuth } from "@/lib/auth";
import { useChat } from "@/lib/chat";
import { api } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { analytics } from "@/lib/analytics";

const Tab = ({ id, label, icon: Icon, active, badge, onClick }) => (
  <button className={`tc-tabbar__item${active ? " is-active" : ""}`} onClick={onClick} data-testid={`mtab-${id}`} aria-label={label}>
    <span className="tc-tabbar__icon"><Icon size={22} strokeWidth={active ? 2.4 : 2} />{badge ? <span className="tc-tabbar__badge" data-testid={`mtab-${id}-badge`}>{badge > 99 ? "99+" : badge}</span> : null}</span>
    <span className="tc-tabbar__label">{label}</span>
  </button>
);

export const MobileTabBar = ({ view }) => {
  const navigate = useNavigate();
  const { user, workspace, logout, setUser } = useAuth();
  const { setModal, me, can, totalMentions, dmUnread } = useChat();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const go = (path, id) => { analytics.track("mobile_tab", { category: "navigation", label: id }); navigate(path); };
  const setPresence = async (presence) => {
    const { data } = await api.post("/users/me/presence", { presence });
    setUser(data); setOpen(false);
    toast.success(presence === "away" ? "You're set to away" : "You're back to active");
  };
  const item = (label, Icon, onClick, testid, danger) => (
    <button className={`tc-sheet__item${danger ? " is-danger" : ""}`} onClick={() => { setOpen(false); onClick(); }} data-testid={testid}><Icon size={18} /> {label}</button>
  );
  return (
    <>
      <nav className="tc-tabbar" data-testid="mobile-tabbar">
        <Tab id="home" label="Home" icon={Home} active={view === "home"} onClick={() => go("/client/home", "home")} />
        <Tab id="dms" label="DMs" icon={MessageCircle} active={view === "dms"} badge={dmUnread} onClick={() => go("/client/dms", "dms")} />
        <Tab id="activity" label="Activity" icon={Bell} active={view === "activity"} badge={totalMentions} onClick={() => go("/client/activity", "activity")} />
        <Tab id="later" label="Later" icon={Bookmark} active={view === "later"} onClick={() => go("/client/later", "later")} />
        <Tab id="you" label="You" icon={UserRound} active={open} onClick={() => setOpen(true)} />
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="tc-sheet" data-testid="mobile-you-sheet">
          <SheetTitle className="sr-only">You</SheetTitle>
          <div className="tc-sheet__head">
            <Avatar user={user} size={48} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>{user?.display_name}</div>
              <div style={{ fontSize: 13, color: "var(--tc-text-muted)", display: "flex", alignItems: "center", gap: 4 }}><Presence presence={user?.presence} /> {user?.presence === "active" ? "Active" : "Away"} · {workspace?.name}</div>
            </div>
          </div>
          <div className="tc-sheet__grid">
            {item("Threads", MessageSquareText, () => navigate("/client/threads"), "msheet-threads")}
            {item("Huddles", Headphones, () => navigate("/client/huddles"), "msheet-huddles")}
            {item("Drafts & sent", Send, () => navigate("/client/drafts"), "msheet-drafts")}
            {item(theme === "dark" ? "Light mode" : "Dark mode", theme === "dark" ? Sun : Moon, () => setModal({ type: "preferences", tab: "appearance" }), "msheet-theme")}
          </div>
          <div className="tc-sheet__list">
            {item("Update your status", UserRound, () => setModal({ type: "editProfile" }), "msheet-status")}
            {item(`Set yourself as ${user?.manual_presence === "away" ? "active" : "away"}`, Presence_, () => setPresence(user?.manual_presence === "away" ? "auto" : "away"), "msheet-presence")}
            {item("Preferences", Bell, () => setModal({ type: "preferences" }), "msheet-preferences")}
            {can("admin.console") && item("Admin & tools", ShieldCheck, () => navigate("/admin"), "msheet-admin")}
            {item(`Sign out of ${workspace?.name || "Total Chat"}`, Home, () => { logout(); navigate("/login"); }, "msheet-signout", true)}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

const Presence_ = ({ size }) => <span style={{ width: size, height: size, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><span style={{ width: 10, height: 10, borderRadius: "50%", background: "var(--tc-green, #2bac76)" }} /></span>;
